import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import axios from "axios";
import CropEditor from "./CropEditor";
import {
  Camera,
  Upload,
  X,
  RotateCcw,
  Check,
  Plus,
  FileText,
  Trash2,
  ScanLine,
  Loader2,
  Image as ImageIcon,
  Settings2,
  Crop,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentScanner({ 
  isOpen, 
  onClose, 
  onScanComplete,
  transactionId = null,
  description = ""
}) {
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [currentDescription, setCurrentDescription] = useState(description);
  const [scannedPdf, setScannedPdf] = useState(null);
  
  // Scan ayarları - Enhanced mod en iyi sonucu veriyor
  const [scanMode, setScanMode] = useState("enhanced"); // bw, grayscale, enhanced, original
  const [quality, setQuality] = useState("high"); // low, medium, high
  const [autoCrop, setAutoCrop] = useState(true);
  
  // Manuel crop state
  const [cropMode, setCropMode] = useState(false);
  const [cropImageIndex, setCropImageIndex] = useState(null);
  const [imageCorners, setImageCorners] = useState({}); // {index: [[x,y], [x,y], [x,y], [x,y]]}
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  // Kamera başlat
  const startCamera = async () => {
    setCameraLoading(true);
    try {
      // Önce mevcut stream'i temizle
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        
        // Video hazır olduğunda
        video.onloadeddata = () => {
          console.log("Video data loaded");
          video.play()
            .then(() => {
              console.log("Video playing");
              setCameraActive(true);
              setCameraLoading(false);
            })
            .catch(err => {
              console.error("Video play error:", err);
              setCameraLoading(false);
              toast.error("Video konnte nicht gestartet werden");
            });
        };

        video.onerror = (err) => {
          console.error("Video error:", err);
          setCameraLoading(false);
          toast.error("Video-Fehler aufgetreten");
        };
      }
    } catch (err) {
      console.error("Kamera erişim hatası:", err);
      setCameraLoading(false);
      if (err.name === 'NotAllowedError') {
        toast.error("Kamera erişimi reddedildi. Lütfen tarayıcı ayarlarından izin verin.");
      } else if (err.name === 'NotFoundError') {
        toast.error("Kamera bulunamadı. Lütfen bir kamera bağlayın.");
      } else if (err.name === 'NotReadableError') {
        toast.error("Kamera başka bir uygulama tarafından kullanılıyor olabilir.");
      } else {
        toast.error("Kamera erişimi başarısız: " + err.message);
      }
    }
  };

  // Kamera durdur
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setCameraLoading(false);
  }, []);

  // Dialog kapandığında kamerayı durdur
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setImages([]);
      setPreviewImages([]);
      setScannedPdf(null);
      setCameraLoading(false);
      setCropMode(false);
      setCropImageIndex(null);
      setImageCorners({});
    }
  }, [isOpen, stopCamera]);

  // Crop onaylandığında
  const handleCropConfirm = (corners) => {
    if (cropImageIndex !== null) {
      // Köşeleri kaydet
      setImageCorners(prev => ({
        ...prev,
        [cropImageIndex]: corners
      }));
      toast.success("Zuschnitt gespeichert");
    }
    setCropMode(false);
    setCropImageIndex(null);
  };

  // Crop iptal
  const handleCropCancel = () => {
    setCropMode(false);
    setCropImageIndex(null);
  };

  // Manuel crop modunu aç - kamerayı durdur
  const openCropEditor = (index) => {
    // Önce kamerayı durdur (varsa)
    if (cameraActive || streamRef.current) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      setCameraActive(false);
      setCameraLoading(false);
    }
    
    // State'leri sırayla güncelle
    setCropImageIndex(index);
    setCropMode(true);
  };

  // Fotoğraf çek
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error("Kamera nicht bereit");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Video boyutlarını kontrol et
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("Video noch nicht geladen. Bitte warten...");
      return;
    }
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    
    setImages(prev => [...prev, imageData]);
    setPreviewImages(prev => [...prev, imageData]);
    
    toast.success(`Seite ${images.length + 1} aufgenommen`);
  };

  // Dosya yükle
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} ist kein Bild`);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, reader.result]);
        setPreviewImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
    
    // Input'u sıfırla
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Görüntü sil
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
    // Köşe bilgisini de sil
    setImageCorners(prev => {
      const newCorners = { ...prev };
      delete newCorners[index];
      return newCorners;
    });
  };

  // Tarama işlemini başlat
  const startScan = async () => {
    if (images.length === 0) {
      toast.error("Bitte fügen Sie mindestens ein Bild hinzu");
      return;
    }

    if (!currentDescription.trim()) {
      toast.error("Bitte geben Sie eine Beschreibung ein");
      return;
    }

    setScanning(true);
    
    try {
      // Manuel köşeler varsa onları da gönder
      const cornersArray = images.map((_, idx) => {
        const corners = imageCorners[idx];
        if (corners) {
          // Köşeleri [x, y] formatına çevir
          return corners.map(c => [c.x, c.y]);
        }
        return null;
      });

      const response = await axios.post(
        `${API_URL}/api/documents/scan`,
        {
          images: images,
          description: currentDescription,
          transaction_id: transactionId,
          scan_mode: scanMode,
          quality: quality,
          auto_crop: autoCrop,
          manual_corners: cornersArray
        },
        { headers: getAuthHeaders() }
      );
      
      setScannedPdf(response.data);
      toast.success(`PDF mit ${response.data.page_count} Seite(n) erstellt`);
      
      // Callback çağır
      if (onScanComplete) {
        onScanComplete(response.data);
      }
      
    } catch (error) {
      console.error("Scan error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Scannen");
    } finally {
      setScanning(false);
    }
  };

  // PDF indir
  const downloadPdf = () => {
    if (!scannedPdf) return;
    
    const link = document.createElement('a');
    link.href = scannedPdf.pdf_base64;
    link.download = scannedPdf.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-[#c11718]" />
            Beleg scannen
          </DialogTitle>
          <DialogDescription>
            Fotografieren oder laden Sie Belege hoch. Die Bilder werden automatisch in ein sauberes Scan-PDF umgewandelt.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Beschreibung */}
          <div>
            <Label>Beschreibung *</Label>
            <Input
              value={currentDescription}
              onChange={(e) => setCurrentDescription(e.target.value)}
              placeholder="z.B. Rechnung Bürobedarf"
              className="mt-1"
            />
          </div>

          {/* Scan Ayarları */}
          <div className="p-3 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-2 mb-3">
              <Settings2 className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Scan-Einstellungen</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Scan-Modus</Label>
                <Select value={scanMode} onValueChange={setScanMode}>
                  <SelectTrigger className="mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enhanced">Verbessert (empfohlen)</SelectItem>
                    <SelectItem value="grayscale">Graustufen</SelectItem>
                    <SelectItem value="bw">Schwarz-Weiß</SelectItem>
                    <SelectItem value="original">Original</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Qualität</Label>
                <Select value={quality} onValueChange={setQuality}>
                  <SelectTrigger className="mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">Hoch (300 DPI)</SelectItem>
                    <SelectItem value="medium">Mittel (200 DPI)</SelectItem>
                    <SelectItem value="low">Niedrig (150 DPI)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between sm:justify-start sm:flex-col sm:items-start">
                <Label className="text-xs text-slate-500">Auto-Zuschnitt</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Switch 
                    checked={autoCrop} 
                    onCheckedChange={setAutoCrop}
                    className="data-[state=checked]:bg-[#c11718]"
                  />
                  <span className="text-xs text-slate-500">{autoCrop ? 'An' : 'Aus'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Gizli video ve canvas elementleri - her zaman DOM'da */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ 
              display: (cameraActive && !cropMode) ? 'block' : 'none',
              width: '100%', 
              height: '400px', 
              objectFit: 'cover',
              backgroundColor: '#000',
              borderRadius: '8px'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Kamera / Upload Bereich */}
          {!scannedPdf && !cropMode && (
            <>
              {cameraActive ? (
                <div className="space-y-4">
                  {/* Kamera Kontrolleri */}
                  <div className="flex justify-center gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={stopCamera}
                    >
                      <X className="h-5 w-5 mr-2" />
                      Schließen
                    </Button>
                    <Button
                      size="lg"
                      className="bg-[#c11718] hover:bg-[#a11415]"
                      onClick={capturePhoto}
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Aufnehmen
                    </Button>
                  </div>

                  {/* Kamera açıkken de çekilen görüntüleri göster */}
                  {previewImages.length > 0 && (
                    <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Aufgenommene Bilder ({previewImages.length})</Label>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {previewImages.map((img, idx) => (
                          <div 
                            key={idx} 
                            className="relative group cursor-pointer"
                            onClick={() => {
                              stopCamera();
                              openCropEditor(idx);
                            }}
                          >
                            <img
                              src={img}
                              alt={`Seite ${idx + 1}`}
                              className={`w-full h-16 object-cover rounded border ${imageCorners[idx] ? 'border-green-500 border-2' : ''}`}
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-1 pointer-events-none">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  stopCamera();
                                  openCropEditor(idx);
                                }}
                                className="p-1 bg-blue-500 rounded-full text-white hover:bg-blue-600 pointer-events-auto"
                                title="Zuschneiden"
                              >
                                <Crop className="h-3 w-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(idx);
                                }}
                                className="p-1 bg-red-500 rounded-full text-white hover:bg-red-600 pointer-events-auto"
                                title="Löschen"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                            <span className="absolute bottom-0.5 left-0.5 bg-black/70 text-white text-xs px-1 rounded pointer-events-none">
                              {idx + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500">
                        Resme tıklayarak kırpma yapabilirsiniz
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Mobil Kamera - capture ile (mobilde native kamera açar) */}
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg hover:border-[#c11718] hover:bg-red-50 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <Camera className="h-10 w-10 text-[#c11718] mb-2" />
                    <span className="text-sm font-medium text-slate-700">Foto aufnehmen</span>
                    <span className="text-xs text-slate-400 mt-1">Kamera direkt öffnen</span>
                  </label>

                  {/* Live Kamera Butonu */}
                  <button
                    onClick={startCamera}
                    disabled={cameraLoading}
                    className="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg hover:border-[#c11718] hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {cameraLoading ? (
                      <>
                        <Loader2 className="h-10 w-10 text-slate-400 mb-2 animate-spin" />
                        <span className="text-sm font-medium text-slate-600">Wird gestartet...</span>
                      </>
                    ) : (
                      <>
                        <ScanLine className="h-10 w-10 text-slate-400 mb-2" />
                        <span className="text-sm font-medium text-slate-600">Live-Kamera</span>
                        <span className="text-xs text-slate-400 mt-1">Mehrere Fotos</span>
                      </>
                    )}
                  </button>

                  {/* Dosya Yükle Butonu */}
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg hover:border-[#c11718] hover:bg-red-50 transition-colors cursor-pointer">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <Upload className="h-10 w-10 text-slate-400 mb-2" />
                    <span className="text-sm font-medium text-slate-600">Bilder hochladen</span>
                    <span className="text-xs text-slate-400 mt-1">Mehrere Bilder möglich</span>
                  </label>
                </div>
              )}

              {/* Yüklenen Görüntüler */}
              {previewImages.length > 0 && !cropMode && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Hochgeladene Bilder ({previewImages.length})</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setImages([]);
                        setPreviewImages([]);
                        setImageCorners({});
                      }}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Alle löschen
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {previewImages.map((img, idx) => (
                      <div 
                        key={idx} 
                        className="relative group cursor-pointer"
                        onClick={() => openCropEditor(idx)}
                      >
                        <img
                          src={img}
                          alt={`Seite ${idx + 1}`}
                          className={`w-full h-24 object-cover rounded border ${imageCorners[idx] ? 'border-green-500 border-2' : ''}`}
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-1 pointer-events-none">
                          {/* Crop butonu */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openCropEditor(idx);
                            }}
                            className="p-1.5 bg-blue-500 rounded-full text-white hover:bg-blue-600 pointer-events-auto"
                            title="Zuschneiden"
                          >
                            <Crop className="h-3.5 w-3.5" />
                          </button>
                          {/* Sil butonu */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(idx);
                            }}
                            className="p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 pointer-events-auto"
                            title="Löschen"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded pointer-events-none">
                          {idx + 1}
                        </span>
                        {/* Crop edilmiş göstergesi */}
                        {imageCorners[idx] && (
                          <span className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center pointer-events-none">
                            <Crop className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    ))}
                    
                    {/* Daha fazla ekle */}
                    <label className="flex items-center justify-center h-24 border-2 border-dashed rounded cursor-pointer hover:border-[#c11718] hover:bg-red-50">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                      <Plus className="h-6 w-6 text-slate-400" />
                    </label>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-1">
                    <Crop className="h-3 w-3 inline mr-1" />
                    Resme tıklayarak manuel kırpma yapabilirsiniz
                  </p>
                </div>
              )}
            </>
          )}

          {/* Manuel Crop Editor - Ayrı blokta olmalı */}
          {cropMode && cropImageIndex !== null && previewImages[cropImageIndex] && (
            <CropEditor
              imageData={previewImages[cropImageIndex]}
              onConfirm={handleCropConfirm}
              onCancel={handleCropCancel}
              initialCorners={imageCorners[cropImageIndex]}
            />
          )}

          {/* Taranmış PDF Önizleme */}
          {scannedPdf && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">PDF erfolgreich erstellt</p>
                  <p className="text-sm text-green-600">{scannedPdf.filename} • {scannedPdf.page_count} Seite(n)</p>
                </div>
              </div>
              
              <div className="border rounded-lg overflow-hidden bg-slate-100">
                <iframe
                  src={scannedPdf.pdf_base64}
                  className="w-full h-[400px]"
                  title="Scanned PDF Preview"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          {scannedPdf ? (
            <>
              <Button variant="outline" onClick={() => {
                setScannedPdf(null);
                setImages([]);
                setPreviewImages([]);
              }}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Neuer Scan
              </Button>
              <Button variant="outline" onClick={downloadPdf}>
                <FileText className="h-4 w-4 mr-2" />
                PDF herunterladen
              </Button>
              <Button 
                onClick={() => {
                  onClose();
                  toast.success("Beleg wurde gespeichert");
                }}
                className="bg-[#c11718] hover:bg-[#a11415]"
              >
                <Check className="h-4 w-4 mr-2" />
                Fertig
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                onClick={startScan}
                disabled={scanning || images.length === 0}
                className="bg-[#c11718] hover:bg-[#a11415]"
              >
                {scanning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird gescannt...
                  </>
                ) : (
                  <>
                    <ScanLine className="h-4 w-4 mr-2" />
                    Scannen ({images.length} Bild{images.length !== 1 ? 'er' : ''})
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
